import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { ArrowRight, ArrowUpRight, Check, Users, Calendar, BookOpen, MessageCircle, ChevronDown, Star, Quote, Heart, Sparkles } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";

const LandingPage = () => {
  const { loginWithGoogle, user } = useAuth();
  const [email, setEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [membershipTiers, setMembershipTiers] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/testimonials`),
      axios.get(`${API}/membership-tiers`),
      axios.get(`${API}/events?limit=3`),
      axios.get(`${API}/partners`),
    ]).then(([testimonialsRes, tiersRes, eventsRes, partnersRes]) => {
      setTestimonials(testimonialsRes.data);
      setMembershipTiers(tiersRes.data);
      setUpcomingEvents(eventsRes.data);
      setPartners(partnersRes.data);
    }).catch(() => {});
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setNewsletterLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success("Welcome to the ensemble! Check your inbox.");
      setEmail("");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setNewsletterLoading(false);
    }
  };

  const pillars = [
    {
      icon: Users,
      title: "Community",
      description: "Connect with creative women across industries. Find collaborators, mentors, and friends who understand your journey."
    },
    {
      icon: Calendar,
      title: "Events",
      description: "Monthly meetups, workshops, and gatherings designed for connection, creativity, and skill-building."
    },
    {
      icon: BookOpen,
      title: "Resources",
      description: "Access guides, templates, and tools created by and for creative professionals."
    },
    {
      icon: MessageCircle,
      title: "Collaboration",
      description: "Find project partners, get feedback, and access opportunities through our member network."
    }
  ];

  const faqItems = [
    {
      question: "Who is The Creative Ensemble for?",
      answer: "The Creative Ensemble is for female and gender diverse creatives and professionals in creative, entrepreneurial, and strategic fields. This includes writers, designers, filmmakers, artists, founders, consultants, freelancers, and anyone transitioning careers or launching creative projects."
    },
    {
      question: "What makes this different from other communities?",
      answer: "We're not just a networking group or a content library. We're a creative studio meets professional network meets learning platform. We focus on real collaboration, genuine connections, and tangible opportunities."
    },
    {
      question: "What if I'm just starting out?",
      answer: "Perfect! Many of our members are in transition or building something new. The community is designed to support female and gender diverse creatives and professionals at all stages of their creative journey."
    },
    {
      question: "Can I attend events without a paid membership?",
      answer: "Yes! We offer free community events as well as members-only workshops and programming. Join our free tier to start attending events right away."
    },
    {
      question: "How do I get the most out of membership?",
      answer: "Introduce yourself in the community, attend events, engage in discussions, and don't be afraid to share your work and ask for help. The more you put in, the more you get out."
    }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#FF2D87]" data-testid="hero-section">
        <div className="container-main py-20 md:py-32 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7">
              <p className="overline text-white mb-6 fade-in">A Creative & Professional Network</p>
              <h1 className="heading-hero mb-8 fade-in stagger-1 text-white">
                Where creative women{" "}
                <span className="font-display text-[#FFC2E8]">connect</span>,{" "}
                <span className="font-display text-[#FFC2E8]">collaborate</span>,{" "}
                and grow.
              </h1>
              <p className="text-lg md:text-xl text-white max-w-xl mb-10 fade-in stagger-2">
                A curated network for female and gender diverse creatives and professionals in creative, entrepreneurial, and strategic fields. 
                Events, workshops, resources, and real opportunities—together.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 fade-in stagger-3">
                {user ? (
                  <Link to="/home" className="bg-[#0A0A0A] text-white font-bold rounded-full px-8 py-4 hover:bg-[#5170FF] transition-colors text-sm uppercase tracking-widest flex items-center justify-center gap-2" data-testid="hero-dashboard-btn">
                    Go to Dashboard
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <Link to="/login" className="bg-[#0A0A0A] text-white font-bold rounded-full px-8 py-4 hover:bg-[#5170FF] transition-colors text-sm uppercase tracking-widest flex items-center justify-center gap-2" data-testid="hero-join-btn">
                    Join The Ensemble
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
                <Link to="/events" className="bg-transparent text-white border-2 border-white font-bold rounded-full px-8 py-4 hover:bg-white hover:text-[#FF2D87] transition-colors text-sm uppercase tracking-widest flex items-center justify-center gap-2" data-testid="hero-events-btn">
                  Explore Events
                </Link>
              </div>
            </div>

            {/* Right Image */}
            <div className="lg:col-span-5 relative">
              <div className="border-2 border-[#0A0A0A] shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1587614203976-365c74645e83?w=800&h=600&fit=crop"
                  alt="Female and gender diverse creatives and professionals collaborating"
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
              {/* Floating stats card → Ko-fi Founding Member */}
              <a
                href="https://ko-fi.com/c/f5aa911890"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute -bottom-6 -left-6 bg-white border-2 border-[#0A0A0A] shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] p-6 hidden md:block hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all group"
                data-testid="hero-founding-member-card"
              >
                <p className="text-lg font-bold text-[#FF2D87]" style={{fontFamily: "'Garet', sans-serif"}}>Now Accepting</p>
                <p className="stat-label">Founding Members</p>
                <p className="text-xs text-[#0A0A0A] mt-2 flex items-center gap-1 opacity-70 group-hover:opacity-100">
                  Learn more <ArrowUpRight className="w-3 h-3" />
                </p>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="bg-[#0A0A0A] py-4 overflow-hidden">
        <div className="animate-marquee flex whitespace-nowrap">
          {["set-a", "set-b"].map((setId) => (
            <div key={setId} className="flex items-center gap-8 mx-4">
              {["Create", "Collaborate", "Connect", "Grow", "Inspire", "Build", "Lead", "Thrive"].map((word) => (
                <span key={`${setId}-${word}`} className="text-[#FF2D87] font-display text-xl md:text-2xl uppercase tracking-widest flex items-center gap-8">
                  {word}
                  <Star className="w-4 h-4 text-[#FFC2E8]" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-padding bg-[#FFC2E8]" data-testid="about-section">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <p className="overline text-[#5170FF] mb-4">About The Ensemble</p>
            <h2 className="heading-section mb-6">
              More than a community.<br />
              <span className="text-[#5170FF]">A creative ecosystem.</span>
            </h2>
            <p className="text-lg text-[#0A0A0A]">
              The Creative Ensemble is a female-led network designed to connect female and gender diverse creatives and professionals across creative, 
              entrepreneurial, and strategic fields. We support collaboration, education, mentorship, 
              and access to real opportunities through workshops, consulting, partnerships, and community programming.
            </p>
          </div>

          {/* Pillars Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((pillar, index) => (
              <div key={pillar.title} className="bg-white border-2 border-[#0A0A0A] shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] p-8 transition-all duration-200 hover:-translate-y-1" data-testid={`pillar-${pillar.title.toLowerCase()}`}>
                <div className={`w-14 h-14 ${index % 2 === 0 ? 'bg-[#5170FF]' : 'bg-[#FFC2E8]'} flex items-center justify-center mb-6`}>
                  <pillar.icon className={`w-7 h-7 ${index % 2 === 0 ? 'text-white' : 'text-[#FF2D87]'}`} />
                </div>
                <h3 className="text-xl font-display font-bold mb-3">{pillar.title}</h3>
                <p className="text-[#4A4A4A] text-sm">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Events Preview */}
      {upcomingEvents.length > 0 && (
        <section className="section-padding bg-[#FFC2E8]" data-testid="events-preview">
          <div className="container-main">
            <div className="flex justify-between items-end mb-12">
              <div>
                <p className="overline text-[#FF2D87] mb-4">Upcoming</p>
                <h2 className="heading-section">Events & Workshops</h2>
              </div>
              <Link to="/events" className="hidden md:flex items-center gap-2 text-[#5170FF] font-bold hover:text-[#FF2D87] transition-colors">
                View All Events
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="space-y-0">
              {upcomingEvents.map((event, index) => (
                <div key={event.event_id} className="event-card" data-testid={`event-preview-${index}`}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
                    <div className="md:col-span-2">
                      <p className="event-date font-mono">
                        {new Date(event.date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric"
                        })}
                      </p>
                    </div>
                    <div className="md:col-span-7">
                      <h3 className="text-2xl font-display font-bold mb-2">{event.title}</h3>
                      <p className="text-[#4A4A4A]">{event.location}</p>
                    </div>
                    <div className="md:col-span-3 flex justify-end">
                      <Link to={`/events`} className="btn-secondary text-xs py-2 px-6">
                        Learn More
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Link to="/events" className="md:hidden flex items-center justify-center gap-2 mt-8 text-[#5170FF] font-bold">
              View All Events
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="section-padding bg-[#0A0A0A] text-white" data-testid="testimonials-section">
          <div className="container-main">
            <div className="text-center mb-16">
              <p className="overline text-[#FF2D87] mb-4">Testimonials</p>
              <h2 className="heading-section">What members are saying</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={testimonial.testimonial_id} className="relative" data-testid={`testimonial-${index}`}>
                  <Quote className="w-10 h-10 text-[#FF2D87] mb-6" />
                  <p className="text-lg mb-8 text-[#E5E5E5]">{testimonial.content}</p>
                  <div className="flex items-center gap-4">
                    {testimonial.image_url && (
                      <img
                        src={testimonial.image_url}
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full border-2 border-[#FF2D87] object-cover"
                      />
                    )}
                    <div>
                      <p className="font-bold">{testimonial.name}</p>
                      <p className="text-sm text-[#A0A0A0]">{testimonial.title}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Membership Tiers */}
      <section id="membership" className="section-padding bg-white" data-testid="membership-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="overline text-[#FF2D87] mb-4">Membership</p>
            <h2 className="heading-section mb-6">Choose your path</h2>
            <p className="text-lg text-[#4A4A4A] max-w-2xl mx-auto">
              Start free today. Paid tiers with deeper community access launching soon —
              sign up below to be the first to know.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {membershipTiers.map((tier) => {
              const isFree = tier.price_monthly === 0;
              const accent = isFree ? "bg-white" : "bg-[#FFC2E8]";
              return (
                <div
                  key={tier.tier_id}
                  className={`${accent} border-2 border-[#0A0A0A] shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] p-8 flex flex-col h-full min-h-[420px]`}
                  data-testid={`tier-${tier.name.toLowerCase()}`}
                >
                  <h3 className="text-2xl font-display font-bold mb-3">{tier.name}</h3>
                  <div className="mb-6">
                    {isFree ? (
                      <span className="text-4xl font-bold">Free</span>
                    ) : (
                      <span className="text-2xl font-bold text-[#FF2D87]">Launching Soon</span>
                    )}
                  </div>
                  <p className="text-sm text-[#0A0A0A] mb-8 flex-1">
                    {isFree
                      ? "Join the community. Browse the directory, attend free events, and get a profile."
                      : "Premium membership tier with deeper benefits and access. Be the first to know when it opens."}
                  </p>
                  {isFree ? (
                    <Link
                      to="/login"
                      className="bg-[#0A0A0A] text-white font-bold rounded-full px-6 py-3 hover:bg-[#FF2D87] transition-colors text-xs uppercase tracking-widest text-center"
                      data-testid={`tier-${tier.name.toLowerCase()}-cta`}
                    >
                      Join Free
                    </Link>
                  ) : (
                    <button
                      onClick={() => {
                        const newsletterSection = document.querySelector('[data-testid="newsletter-section"]');
                        if (newsletterSection) newsletterSection.scrollIntoView({ behavior: "smooth" });
                      }}
                      className="bg-white border-2 border-[#0A0A0A] text-[#0A0A0A] font-bold rounded-full px-6 py-3 hover:bg-[#0A0A0A] hover:text-white transition-colors text-xs uppercase tracking-widest"
                      data-testid={`tier-${tier.name.toLowerCase()}-cta`}
                    >
                      Sign Up for Interest
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="bg-[#FF2D87] py-20" data-testid="newsletter-section">
        <div className="container-main">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-6">
              Stay in the loop
            </h2>
            <p className="text-white mb-8">
              Get updates on events, opportunities, and community news delivered to your inbox.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 px-6 py-4 bg-white border-2 border-white text-[#0A0A0A] placeholder:text-[#4A4A4A] focus:outline-none"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={newsletterLoading}
                className="btn-tertiary whitespace-nowrap"
                data-testid="newsletter-submit-btn"
              >
                {newsletterLoading ? "..." : "Subscribe"}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Our Partners — auto-hides when empty */}
      {partners.length > 0 && (
        <section className="section-padding bg-white" data-testid="partners-section">
          <div className="container-main">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-16">
                <p className="overline text-[#5170FF] mb-4 inline-flex items-center gap-2 justify-center">
                  <Heart className="w-4 h-4" /> Our Partners
                </p>
                <h2 className="heading-section mb-6">
                  Backed by brands<br />who believe in <span className="text-[#FF2D87]">connection.</span>
                </h2>
                <p className="text-[#4A4A4A] max-w-2xl mx-auto">
                  The Creative Ensemble is supported by a growing roster of partners helping us host events,
                  build programming, and amplify female and gender diverse creatives and professionals.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {partners.map((partner) => {
                  const card = (
                    <div className="bg-white border-2 border-[#0A0A0A] shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] aspect-[4/3] flex items-center justify-center p-8 hover:translate-x-1 hover:translate-y-1 hover:shadow-[3px_3px_0px_0px_rgba(10,10,10,1)] transition-all">
                      <img
                        src={partner.logo_url}
                        alt={partner.name}
                        className="max-w-full max-h-full object-contain"
                        loading="lazy"
                      />
                    </div>
                  );
                  return partner.website ? (
                    <a
                      key={partner.partner_id}
                      href={partner.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={partner.name}
                      data-testid={`partner-${partner.partner_id}`}
                    >
                      {card}
                    </a>
                  ) : (
                    <div key={partner.partner_id} aria-label={partner.name} data-testid={`partner-${partner.partner_id}`}>
                      {card}
                    </div>
                  );
                })}
              </div>

              <p className="text-center mt-12 text-sm text-[#4A4A4A]">
                Want to support The Creative Ensemble?{" "}
                <a
                  href="https://ko-fi.com/c/b4ab046cde"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-[#FF2D87] hover:underline inline-flex items-center gap-1"
                >
                  Become a Partner <ArrowUpRight className="w-3 h-3" />
                </a>
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Support Us — Ko-fi (no pricing publicly visible) */}
      <section className="section-padding bg-[#0A0A0A] text-white" data-testid="support-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <p className="overline text-[#FFC2E8] mb-4 inline-flex items-center gap-2 justify-center">
              <Heart className="w-4 h-4" /> Support The Creative Ensemble
            </p>
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">
              We're a small team building<br />something big.
            </h2>
            <p className="text-lg text-white/85 mb-10">
              The Creative Ensemble is a female-led, independently funded community in its founding phase.
              Every tip and supporter helps us host events, build programming, and keep the lights on while we grow.
              If our work has resonated with you, we'd love your support.
            </p>
            <a
              href="https://ko-fi.com/creativeensemblemedia/commissions"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#FF2D87] text-white font-bold rounded-full px-8 py-4 hover:bg-white hover:text-[#0A0A0A] transition-colors text-sm uppercase tracking-widest"
              data-testid="kofi-support-link"
            >
              <Heart className="w-4 h-4" /> Support us on Ko-fi
            </a>
            <p className="text-xs text-white/60 mt-4">
              Tips and supporter tiers available on our Ko-fi page.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-[#FFC2E8]" data-testid="faq-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <p className="overline text-[#5170FF] mb-4">FAQ</p>
              <h2 className="heading-section">Common questions</h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqItems.map((item, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="card-brutal px-6"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="py-6 text-left font-display text-xl hover:no-underline">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-[#4A4A4A]">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#FF2D87] py-20" data-testid="final-cta-section">
        <div className="container-main text-center">
          <h2 className="text-3xl md:text-5xl font-display font-bold mb-6 text-white">
            We don't compete.<br />
            We connect.
          </h2>
          <p className="text-lg text-white max-w-xl mx-auto mb-10">
            Ready to join a community that celebrates collaboration over competition? 
            Your creative journey starts here.
          </p>
          <Link to="/login" className="bg-[#0A0A0A] text-white font-bold rounded-full px-8 py-4 hover:bg-[#5170FF] transition-colors text-sm uppercase tracking-widest inline-block" data-testid="final-cta-join-btn">
            Join The Creative Ensemble
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
